import { ChangeEvent, useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { v4 as randomId } from 'uuid';
import { Dayjs } from 'dayjs';
import { AddOutlined } from '@mui/icons-material';

import { validateDecimalNumber, validateNumber } from '../../../../../shared/Helpers/functions';
import { CustomInput, CustomSelect } from '../../../../../shared/uiComponents';
import { useCalculateHoursPerWeek } from '../hook';
import { EditAuthorizationProperties } from './editAuthorizationContent';
import { useSelector } from '../../../../../redux/store';
import './index.scss';

const AddAuthorizationContent = ({
	endDateValue,
	startDateValue,
	update,
	funderId,
}: {
	endDateValue: Dayjs;
	funderId?: string | null;
	startDateValue: Dayjs;
	update: (item: EditAuthorizationProperties) => void;
}) => {
	const sessionTypes = useSelector((state) => state.authorization.insuranceAuthorizationSessionTypes);
	const funderScopedSessionTypes = sessionTypes.filter((x) => (funderId ? x.funderId === funderId : !x.funderId));
	const [typeId, setTypeId] = useState<string>('0');
	const [frequency, setFrequency] = useState<number>(0);
	const [totalHours, setTotalHours] = useState<string>('');
	const [customHoursPerWeek, setCustomHoursPerWeek] = useState<string>('');

	const { hoursPerWeek, loading } = useCalculateHoursPerWeek({
		startDateValue,
		endDateValue,
		totalHours,
		frequency,
	});

	useEffect(() => {
		if (!totalHours.length) return;
		setCustomHoursPerWeek(`${hoursPerWeek}`);
	}, [totalHours, hoursPerWeek]);

	const sessionTypeHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setTypeId(value);
		const frequencyId = funderScopedSessionTypes.find((x) => x.id === value)?.frequencies[0].id;
		if (!frequencyId) return;
		setFrequency(frequencyId);
		setTotalHours('0');
	};

	const frequencyHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		if (!validateNumber(value)) return;
		setFrequency(parseInt(value));
	};

	const onTotalHoursChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateDecimalNumber(value)) return;
		setTotalHours(value);
		setCustomHoursPerWeek('');
	};

	const onHoursPerWeekChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateDecimalNumber(value)) return;
		setCustomHoursPerWeek(value);
	};

	const addDetailHandler = () => {
		if (frequency === 1 && !parseFloat(customHoursPerWeek)) return;
		const selectedType = funderScopedSessionTypes.find((x) => x.id === typeId);
		const data: EditAuthorizationProperties = {
			id: randomId(),
			authorizationSessionTypeId: typeId,
			cptCode: selectedType?.cptCode ?? null,
			frequency,
			totalHours: parseFloat(totalHours),
			hoursPerWeek: frequency === 1 ? parseFloat(customHoursPerWeek) : null,
			sessionTypeId: selectedType?.sessionTypeId ?? null,
		};
		update(data);
		setCustomHoursPerWeek('');
	};

	return (
		<div className="authorizationDetailCard padding8 marginBottom8">
			<div className="sessionTypeRow marginBottom8">
				<div className="fullWidth">
					<CustomSelect
						label="Choose session type:"
						data={funderScopedSessionTypes}
						value={typeId}
						setValue={sessionTypeHandler}
						className="marginBottom16"
					/>
				</div>
				<CustomSelect
					label="Frequency"
					data={
						funderScopedSessionTypes
							.find((x) => x.id === typeId)
							?.frequencies.map((x) => ({
								id: `${x.id}`,
								label: x.name,
							})) as any[]
					}
					className="width150"
					value={`${frequency}`}
					setValue={frequencyHandler}
				/>
			</div>
			<div className="hoursRow marginBottom8">
				<CustomInput label="Total hours" value={totalHours} onChange={onTotalHoursChange} />
				{!loading && (
					<>
						{frequency === 1 && (
							<CustomInput label="Weekly limit" value={customHoursPerWeek} onChange={onHoursPerWeekChange} />
						)}
						{(frequency !== 1 || Boolean(parseFloat(customHoursPerWeek))) && (
							<AddOutlined fontSize="large" onClick={addDetailHandler} className="marginTop24" />
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default AddAuthorizationContent;
